import { PayloadAction } from '@reduxjs/toolkit';

import { City } from 'api/Address/interfaces';
import { IBranchDetail } from 'api/Branches/interfaces';
import { BrandsData, ModelData, VariantData } from 'api/Vehicles/interfaces';
import { RiskCategories } from 'app/domain/loan/constants';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { getItem, LS_KEYS } from 'utils/localStorage';

import {
  Collateral,
  LoanData,
  LoanState,
  VehicleDocumentDetail,
  VehicleInformation,
} from './interfaces';

export const initialState: LoanState = {
  form: {
    brand: {
      is_popular: false,
      brand_code: '',
      brand_name: '',
      brand_image: '',
    },
    model: {
      is_popular: false,
      parent_code: '',
      model_code: '',
      model_name: '',
      model_image: '',
    },
    variant: {
      parent_code: '',
      variant_code: '',
      variant_name: '',
      category_id: '',
      asset_type_id: 'MOTOR',
      rank_number: null,
      is_new: false,
    },
    year: 0,
    city: {
      code: '',
      name: '',
      province_code: '',
    },
    loan: {
      admin_fee: 0,
      asset_insurance_id: '',
      asset_insurance_premium: 0,
      capitalized_ntf_amount: 0,
      effective_rate: 0,
      fiducia_fee: 0,
      life_insurance_id: '',
      life_insurance_premium: 0,
      monthly_installment: 0,
      ntf_amount: 0,
      other_fee: 0,
      provision_fee: 0,
      provision_rate: 0,
      rsa_insurance_calculation_id: '',
      rsa_insurance_premium: 0,
      santunan_insurance_calculation_id: '',
      santunan_insurance_premium: 0,
      tenure: 0,
      tenure_id: '',
      proposed_loan_amount: 0,
      customer_type: 'NEW',
      old_package_id: null,
      package_id: 0,
      product_id: 0,
      flat_rate: 0,
      ltv: 0,
      total_admin_fee: 0,
      flat_rate_monthly: 0,
    },
    collateral: {
      asset_price: 0,
      max_ltv: 0,
      ntf_amount: 0,
    },
    risk_category: RiskCategories.UNCLASSIFIED,
    survey_type: '',
    bpkb_ownership: '',
    bpkb_status: '',
    package_id: 0,
    product_id: 0,
    tax_status: '',
    vehicle_licence_number: '',
    is_stnk_active: null,
    branch: {
      branch_id: 0,
      pos_id: '',
      pos_name: '',
      address: '',
      rt: '',
      rw: '',
      sub_district: '',
      district: '',
      city: '',
      zip_code: '',
      area_phone: '',
      phone: '',
    },
  },
  redirect: {
    isAdditionalPersonalInformationGiven: false,
    isApplicationSubmitted: false,
    isFromFinalEdit: [],
    isKTPUploaded: false,
    isKTPVerified: false,
    isLoanAndVehicleConfirmed: false,
    isLoanEdited: false,
    isOfflineTouchPointConfirmed: false,
    isSelfieVerified: false,
  },
  loan_amount: 0,
  isModalErrorOpen: false,
  isDataNotMatchError: false,
  is_update_to_verified: false,
  is_update_to_vehicle_info_provided: false,
  skip_liveness: false,
  skip_ktp: false,
};

export const loanSlice = createSlice({
  name: 'loan',
  initialState,
  reducers: {
    chooseBrands(state, action: PayloadAction<BrandsData>) {
      state.form.brand = action.payload;
    },
    chooseBrandImage(state, action: PayloadAction<string>) {
      state.form.brand.brand_image = action.payload;
    },
    chooseModel(state, action: PayloadAction<ModelData>) {
      state.form.model = action.payload;
    },
    chooseModelImage(state, action: PayloadAction<string>) {
      state.form.model.model_image = action.payload;
    },
    resetModel(state) {
      state.form.model = initialState.form.model;
    },
    chooseVariant(state, action: PayloadAction<VariantData>) {
      state.form.variant = { ...state.form.variant, ...action.payload };
    },
    resetVariant(state) {
      state.form.variant = initialState.form.variant;
    },
    chooseYear(state, action: PayloadAction<number>) {
      state.form.year = action.payload;
    },
    resetYear(state) {
      state.form.year = initialState.form.year;
    },
    chooseCity(state, action: PayloadAction<City>) {
      state.form.city = action.payload;
    },
    resetCity(state) {
      state.form.city = initialState.form.city;
    },
    chooseBPKBOwnership(state, action: PayloadAction<string>) {
      state.form.bpkb_ownership = action.payload;
    },
    chooseBPKBStatus(state, action: PayloadAction<string>) {
      state.form.bpkb_status = action.payload;
    },
    chooseTaxStatus(state, action: PayloadAction<string>) {
      state.form.tax_status = action.payload;
    },
    chooseStnkStatus(state, action: PayloadAction<boolean>) {
      state.form.is_stnk_active = action.payload;
    },
    updateVehicleLicenseNumber(state, action: PayloadAction<string>) {
      state.form.vehicle_licence_number = action.payload;
    },
    updatePackageId(state, action: PayloadAction<number>) {
      state.form.package_id = action.payload;
    },
    updateProductId(state, action: PayloadAction<number>) {
      state.form.product_id = action.payload;
    },
    confirmLoanAndVehicle(state) {
      state.redirect.isLoanAndVehicleConfirmed = true;
    },
    confirmSelfie(state) {
      state.redirect.isSelfieVerified = true;
    },
    confirmKTP(state) {
      state.redirect.isKTPVerified = true;
    },
    confirmKTPUploaded(state) {
      state.redirect.isKTPUploaded = true;
    },
    removeKTPStatus(state) {
      state.redirect.isKTPVerified = false;
      state.redirect.isKTPUploaded = false;
    },
    confirmPersonalInformation(state) {
      state.redirect.isAdditionalPersonalInformationGiven = true;
    },
    confirmOfflineTouchPoint(state) {
      state.redirect.isOfflineTouchPointConfirmed = true;
    },
    confirmApplicationSubmission(state) {
      state.redirect.isApplicationSubmitted = true;
    },
    updateLoan(state, action: PayloadAction<LoanData>) {
      state.form.loan = action.payload;
      state.loan_amount = action.payload.ntf_amount;
    },
    updateVehicleDocumentsDetail(
      state,
      action: PayloadAction<VehicleDocumentDetail>,
    ) {
      state.form.bpkb_ownership = action.payload.bpkb_ownership;
      state.form.bpkb_status = action.payload.bpkb_status;
      state.form.tax_status = action.payload.tax_status;
      state.form.is_stnk_active = action.payload.is_stnk_active;
    },
    setFromFinalEdit(state, action: PayloadAction<string>) {
      if (
        state.redirect.isFromFinalEdit.length > 0 &&
        state.redirect.isFromFinalEdit.includes(action.payload)
      ) {
        return;
      }
      state.redirect.isFromFinalEdit = [
        ...state.redirect.isFromFinalEdit,
        action.payload,
      ];
    },
    resetFromFinalEdit(state) {
      if (state.redirect.isFromFinalEdit.length === 0) {
        return;
      }
      state.redirect.isFromFinalEdit = state.redirect.isFromFinalEdit.slice(
        0,
        -1,
      );
    },
    resetAllFromFinalEdit(state) {
      state.redirect.isFromFinalEdit = initialState.redirect.isFromFinalEdit;
    },
    updateCollateral(state, action: PayloadAction<Collateral>) {
      state.form.collateral = action.payload;
      state.form.loan.ntf_amount = action.payload.ntf_amount;
    },
    updateCurrentLoanAmount(state, action: PayloadAction<number>) {
      state.loan_amount = action.payload;
    },
    setLoanEdited(state) {
      state.redirect.isLoanEdited = true;
    },
    resetLoanEdited(state) {
      state.redirect.isLoanEdited = false;
    },
    updateRiskCategory(state, action: PayloadAction<RiskCategories>) {
      state.form.risk_category = action.payload;
    },
    updateSurveyType(state, action: PayloadAction<string>) {
      state.form.survey_type = action.payload;
    },
    updateSelectedBranch(state, action: PayloadAction<IBranchDetail>) {
      state.form.branch = action.payload;
    },
    setModalErrorOpen(state) {
      const showNetworkErrorModal = getItem(LS_KEYS.showNetworkErrorModal);
      const isUnauthorizedError = showNetworkErrorModal === 'false';

      if (!isUnauthorizedError) {
        state.isModalErrorOpen = true;
      }
    },
    setModalErrorClose(state) {
      state.isModalErrorOpen = false;
    },
    setIsDataNotMatchError(state) {
      state.isDataNotMatchError = true;
    },
    resetIsDataNotMatchError(state) {
      state.isDataNotMatchError = initialState.isDataNotMatchError;
    },
    resetAllLoan(state) {
      state.form = initialState.form;
      state.isModalErrorOpen = initialState.isModalErrorOpen;
      state.loan_amount = initialState.loan_amount;
      state.redirect = initialState.redirect;
      state.is_update_to_verified = initialState.is_update_to_verified;
    },
    setIsUpdateToVerified(state, action: PayloadAction<boolean>) {
      state.is_update_to_verified = action.payload;
    },
    setIsUpdateToVehicleInfoProvided(state, action: PayloadAction<boolean>) {
      state.is_update_to_vehicle_info_provided = action.payload;
    },
    updateVehicleInformation(state, action: PayloadAction<VehicleInformation>) {
      state.form.brand = action.payload.brand;
      state.form.model = action.payload.model;
      state.form.variant = action.payload.variant;
      state.form.year = action.payload.year;
    },
    resetTenure(state) {
      state.form.loan.tenure = initialState.form.loan.tenure;
      state.form.loan.monthly_installment =
        initialState.form.loan.monthly_installment;
    },
    updateSkipLiveness(state, action: PayloadAction<boolean>) {
      state.skip_liveness = action.payload;
    },
    updateSkipKTP(state, action: PayloadAction<boolean>) {
      state.skip_ktp = action.payload;
    },
  },
});

export const {
  chooseBPKBOwnership,
  chooseBPKBStatus,
  chooseBrandImage,
  chooseBrands,
  chooseCity,
  chooseModel,
  chooseModelImage,
  chooseStnkStatus,
  chooseTaxStatus,
  chooseVariant,
  chooseYear,
  confirmApplicationSubmission,
  confirmKTP,
  confirmKTPUploaded,
  confirmLoanAndVehicle,
  confirmOfflineTouchPoint,
  confirmPersonalInformation,
  confirmSelfie,
  resetAllFromFinalEdit,
  resetAllLoan,
  resetCity,
  resetFromFinalEdit,
  resetIsDataNotMatchError,
  resetLoanEdited,
  resetModel,
  resetVariant,
  resetYear,
  setFromFinalEdit,
  setIsDataNotMatchError,
  setLoanEdited,
  setModalErrorClose,
  setModalErrorOpen,
  setIsUpdateToVerified,
  updateCollateral,
  updateCurrentLoanAmount,
  updateLoan,
  updatePackageId,
  updateProductId,
  updateRiskCategory,
  updateSelectedBranch,
  updateSurveyType,
  updateVehicleDocumentsDetail,
  updateVehicleInformation,
  updateVehicleLicenseNumber,
  updateSkipLiveness,
  updateSkipKTP,
  removeKTPStatus,
  setIsUpdateToVehicleInfoProvided,
  resetTenure,
} = loanSlice.actions;

export default loanSlice.reducer;
